import { keepPreviousData, useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import { Button, Flex, Form, Input, Modal, Pagination, Radio, Table, Typography } from "antd"
import { useAuth } from "../hooks/useAuth";
import { CreateOrganisation, Organisation } from "../interfaces/Organisation.interface";
import { PaginationOptions, PaginationResult } from "../interfaces/PaginationOptions.interface";
import { ReactNode, useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, FolderOpenOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { AnyObject } from "antd/es/_util/type";
import { useForm } from "antd/es/form/Form";
import { ColumnsType } from "antd/es/table";
import { OrgDetail } from "../components/OrgDetail";
import {Campaign} from "../interfaces/Campaign.interface";


const getOrganisationColumns = ()=> {
    let preCols = []
    
    preCols.push({ title: 'Id', dataIndex: 'id', key: 'id' })
    preCols.push({ title: 'Name', dataIndex: 'name', key: 'name' })
   
    return [
    ...preCols
    ]
}
export const SystemOrganisations = ()=> {
    const [paginationOptions, setPaginationOptions] = useState<PaginationOptions>({ limit: 100, offset: 0 });
    
    const auth = useAuth();

    const queryClient = useQueryClient();
    const createOrgMutation = useMutation({
        mutationFn: (org: CreateOrganisation) => auth.api.createOrganisation(org),
        onSuccess: async (data:Organisation|null) => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['admin', 'all_organisations'] })
            return data;
        },
        onError: (err:Error)=> {
            console.log("campaign mutation error", err);
            return err;
        }
    })
    return (
        <CRUDUI<Organisation> 
            pluralTitle="Organisations"
            singularTitle="Organisation"
            read={{
                columns:getOrganisationColumns(),
                queryResult:useQuery({ queryKey: ['admin', 'all_organisations', 'offset', paginationOptions.offset, 'limit', paginationOptions.limit], queryFn:()=> auth.api.allOrganisations(paginationOptions), placeholderData: keepPreviousData}),
                onPaginationChange:(options)=>setPaginationOptions(options),
                paginationOptions:paginationOptions,
                getRowId:(row:Organisation)=>row.id ?? ""
            }}
            create={{
                createForm:(<Flex vertical gap={10}>
                    <Form.Item name={"name"} label={"Name"} rules={[
                        { type: "string", min: 3, required: true}
                    ]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name={"appName"} label={"App Name"} rules={[
                        { type: "string", min: 3, required: true}
                    ]}>
                        <Input />
                    </Form.Item>
                    </Flex>),
                onCreate: async (values:any)=>{

                    let org:CreateOrganisation = {
                        name: values['name'],
                        appName: values['appName']
                    };
                    try {
                        let result = await createOrgMutation.mutateAsync(org);
                        console.log('mutateResult', result);
                        queryClient.invalidateQueries({ queryKey: ['auth'] })
                        return true;
                    } catch (mutateErr) {
                        console.error("mutateError", mutateErr);
                    }
                    return false
                }
            }}
            viewDetail={{
                icon: <SettingOutlined />,
                render: (org:Organisation)=><OrgDetail organisationId={org.id ?? ""} />,
                title: (org:Organisation)=><Typography.Title level={3}>{org.name}</Typography.Title>
            }}
         /> 
    )
}

const CRUDUI = <T extends AnyObject,>(props: { 
    pluralTitle: string,
    singularTitle: string,
    read : {
        queryResult: UseQueryResult<PaginationResult<T> | null, Error>,
        onPaginationChange: (options:PaginationOptions)=>void,
        paginationOptions: PaginationOptions,
        columns: ColumnsType<T>,
        getRowId: (row:T)=>string
    },
    create?: {
        createForm: ReactNode,
        onCreate: (values:any)=>Promise<boolean>
    },
    delete?: {
        icon: ReactNode

    },
    viewDetail?: {
        icon: ReactNode,
        render: (data:T)=>ReactNode,
        title: (data:T)=>ReactNode
    },
    edit?: {
        icon: ReactNode
    }
})=> {
    const [currentPage, setCurrentPage] = useState<number>(1);

    const onPaginationChange = (page: number, pageSize: number) => {
        const newOffset = (page - 1) * pageSize;
        props.read.onPaginationChange({ offset: newOffset, limit: pageSize });
        setCurrentPage(page);
    }

    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [createForm] = Form.useForm();
    const [columns, setColumns] = useState<ColumnsType<T>>(props.read.columns);

    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [detailData, setDetailData] = useState<T|null>(null);

    const create = ()=>{
        console.log("create")
        setShowCreateModal(true)
    }

    const cancelCreate = () => {
        setShowCreateModal(false);
    }

    const createSave = async () => {
        let validationSuccess = false;
        try {
            await createForm.validateFields();
            validationSuccess = true;
        } catch (err) {
            validationSuccess = false;
        }
        if(validationSuccess)
        {
            let values =  await createForm.getFieldsValue();
            let result = await props.create?.onCreate(values);
            if(result)
            {
                setShowCreateModal(false);
            }
        }
    }

    const handleAction = (action:string, obj:T)=>{
        console.log("action", action, obj);
        switch(action)
        {
            case "open":
                setDetailData(obj);
                setShowDetailModal(true);
                break;
            case "edit":
                break;
            case "delete":
                break;
        }
    }

    useEffect(()=>{
        if(props.edit || props.delete || props.viewDetail)
        {
            setColumns([
                ...props.read.columns,
                { title: "Actions", dataIndex: '.', key:'view', render: (value: any, obj:T)=>(
                    <Flex>          
                        <Button.Group>
                            {props.viewDetail && <Button type="primary" onClick={()=>handleAction("open", obj)}>{props.viewDetail.icon}</Button>}
                            {props.edit && <Button onClick={()=>handleAction("edit", obj)}>{props.edit.icon}</Button>}
                            {props.delete && <Button danger onClick={()=>handleAction("delete", obj)}>{props.delete.icon}</Button>}
                        </Button.Group>
                    </Flex>)}
            ])
        } else {
            setColumns(props.read.columns);
        }
    },[props.read.columns])

    
    const dismissDetailModal = ()=>{
        setShowDetailModal(false);
    }
    
    return (
        <Flex vertical gap={10}>
            <Flex justify="space-between" align="baseline">
                <Typography.Title level={3}>{props.pluralTitle}</Typography.Title>
                {props.create && <Button type="primary" onClick={create}>
                    <PlusOutlined /> New {props.singularTitle}
                </Button>}
            </Flex>
            <Table
                columns={columns}
                size={"middle"}
                loading={props.read.queryResult.isFetching}
                dataSource={props.read.queryResult.data?.results ?? []}
                rowKey={(rowData: T) => props.read.getRowId(rowData)}
                pagination={false}
                sticky
                >
            </Table>
            <Pagination 
                current={currentPage}
                onChange={onPaginationChange} 
                total={props.read.queryResult.data?.total ?? 0} 
                pageSize={props.read.paginationOptions.limit} 
                pageSizeOptions={[10, 20, 50, 100]} 
                showSizeChanger 
            />
            <Modal 
            
                title={`Create ${props.singularTitle}`}
                open={showCreateModal}
                onOk={createSave}
                okText={"Create"}
                // confirmLoading={confirmLoading}
                onCancel={cancelCreate}
                // width={`99vw`}
                destroyOnClose>
                <Form
                    form={createForm}
                    layout={"vertical"}
                    >
                    {props.create?.createForm}
                </Form>
            </Modal>

            {detailData && <Modal
                style={{
                    width: "85vw",
                    minWidth: "85vw",
                    maxHeight: "80vh"
                }}
                title={props.viewDetail?.title(detailData) ?? ""}
                open={showDetailModal}
                onOk={dismissDetailModal}
                destroyOnClose
                onCancel={dismissDetailModal}
                footer={ (_, { OkBtn, CancelBtn }) => (
                    <>
                    </>
                  )}
                >
            
                {props.viewDetail?.render(detailData)}
            </Modal>}
        </Flex>

    )

}